import { serverEnv } from '@/shared/config/serverEnv'
import type { ArrayComparisonFilter, QueryStringInput } from '@hello-aria/query-string'
import { In, isArrayComparisonFilter, isComparisonFilter, stringifyQueryString } from '@hello-aria/query-string'
import { isNil, get, isEmpty, uniq, chunk } from 'lodash'

import endpoints from '@/assets/__config__/endpoints.json'
import { isAbortError, type Page } from '@hello-aria/makalu-api-client'
import type { RequestResponse } from '@/shared/services/requestServices'

type JoinWithDots<K, P> = K extends string ? (P extends string ? `${K}${'' extends P ? '' : '.'}${P}` : never) : never
type KeepDeepestPaths<T> =
    T extends Record<string, unknown> ? { [K in keyof T]-?: JoinWithDots<K, KeepDeepestPaths<T[K]>> }[keyof T] : ''
export type Endpoints = KeepDeepestPaths<typeof endpoints>

export type ResolveApiUrlOptions = {
    variables?: {
        [name: string]: string | undefined
    }
    query?: QueryStringInput
}

/**
 * Replace all the strings between {{foo}} by the according options key.
 * @param endpoint   Endpoint to add after base URL.
 * @param options    Optional object that contains key/value pairs to replace endpoint dynamic properties.
 * @returns {string} The formatted url.
 */

// makalu-api calls go through the typed client. The keys left call Pennylane and makalu's own route handlers.
const resolveBaseUrl = (key: Endpoints) => {
    if (key.startsWith('pennylane')) return serverEnv.pennylaneBaseUrl

    const isServer = typeof window === 'undefined'
    const absoluteBaseUrl = isServer ? serverEnv.auth0BaseUrl : ''
    return `${absoluteBaseUrl}/api`
}

export const resolveApiUrl = (key: Endpoints, options?: ResolveApiUrlOptions): string => {
    const { variables, query } = options || {}
    const endpoint = get(endpoints, key)
    let formattedUrl = `${resolveBaseUrl(key)}${endpoint}`

    if (!isNil(variables)) {
        Object.keys(variables).forEach((variableKey) => {
            const value = variables[variableKey]
            if (value && formattedUrl.includes(`{{${variableKey}}}`)) {
                formattedUrl = formattedUrl.replace(new RegExp(`{{${variableKey}}}`, 'g'), value)
            }
        })
    }

    const stringifiedParams = toQueryString(query)
    const operator = isEmpty(stringifiedParams) ? '' : '?'
    return formattedUrl + operator + stringifiedParams
}

const toQueryString = (query: QueryStringInput | undefined): string => {
    const { sorts, pagination, filters, params } = query || {}
    return stringifyQueryString({ sorts, pagination, filters, params }) ?? ''
}

/*
 * `resolveApiUrl` left a missing variable as `{{name}}` in the URL, and makalu-api answered an error.
 * The service now stops before the request, with an error that names the parameter (ERR-6).
 */
export const requirePathParam = (name: string, value: string | null | undefined): string => {
    if (isNil(value) || value.trim() === '') throw new Error(`The path parameter ${name} is blank`)

    return value
}

type InFilterOptions = {
    filterProperty?: string
}

export function createInFilter<T extends readonly unknown[]>(
    items: T | undefined,
    propertyAccessor: (item: T[number]) => string | null | undefined,
    options: InFilterOptions = {}
): ArrayComparisonFilter[] {
    if (!items || isEmpty(items)) {
        return []
    }

    const values = uniq(items.map(propertyAccessor).filter((value): value is string => value != null))

    if (isEmpty(values)) {
        return []
    }

    const filterProperty = options.filterProperty || 'id'
    return [In(filterProperty, values)]
}

const EXPORT_BATCH_CONCURRENCY = 5
const BATCH_FETCH_RETRY_COUNT = 1
const DEFAULT_BATCH_SIZE = 100
/** The api caps pages at 100 rows and falls back to 20 when no page size is asked for. */
const API_MAX_PAGE_SIZE = 100

const fetchWithRetry = async <T>(fetcher: () => Promise<T>, retries = BATCH_FETCH_RETRY_COUNT): Promise<T> => {
    try {
        return await fetcher()
    } catch (error) {
        if (isAbortError(error) || retries === 0) {
            throw error
        }
        return fetchWithRetry(fetcher, retries - 1)
    }
}

/**
 * Creates a wrapper around a fetcher function that automatically batches large array filter values
 * into smaller chunks to avoid potential API limitations.
 *
 * @template TParams - The type of parameters accepted by the fetcher function, must include an optional query property
 * @template TItem - The type of items returned in the response body
 *
 * @param {function} fetcher - The original fetch function that retrieves data
 * @param {number} [batchSize=100] - Maximum number of items to include in each batch request
 *
 * @returns {function} A wrapped version of the fetcher that handles batching automatically
 *                     and combines results from multiple requests
 *
 * @example
 * // Create a batched version of your API fetcher
 * const batchedUserFetcher = batchFetch(fetchUsers, 50);
 *
 * // Use it like the original fetcher - batching happens automatically when needed
 * const result = await batchedUserFetcher({
 *   query: {
 *     filters: [{ field: 'id', operator: 'in', value: largeArrayOfIds }]
 *   }
 * });
 */
export function batchFetch<TParams extends { query?: QueryStringInput }, TItem>(
    fetcher: (p: TParams) => Promise<Page<TItem>>,
    defaultBatchSize?: number
): (params: TParams) => Promise<Page<TItem>>
export function batchFetch<TParams extends { query?: QueryStringInput }, TItem>(
    fetcher: (p: TParams) => Promise<RequestResponse<TItem[]> | undefined>,
    defaultBatchSize?: number
): (params: TParams) => Promise<RequestResponse<TItem[]> | undefined>
export function batchFetch<TParams extends { query?: QueryStringInput }, TItem>(
    fetcher: (p: TParams) => Promise<{ body?: TItem[] } | undefined>,
    defaultBatchSize = DEFAULT_BATCH_SIZE
) {
    return async (params: TParams): Promise<{ body?: TItem[] } | undefined> => {
        const { query } = params
        const filters = query?.filters ?? []

        const pagination = query?.pagination
        const explicitPageSize = pagination && ('first' in pagination ? pagination.first : pagination.last)
        const batchSize = explicitPageSize ? Math.min(explicitPageSize, defaultBatchSize) : defaultBatchSize

        const batchableIndex = filters.findIndex(
            (f) => isComparisonFilter(f) && isArrayComparisonFilter(f) && Array.isArray(f.value)
        )

        const batchableFilter = batchableIndex === -1 ? undefined : filters[batchableIndex]
        if (isNil(batchableFilter) || !isArrayComparisonFilter(batchableFilter)) {
            return fetcher(params)
        }

        // Callers that ask for no page size get the api default page, which is smaller than a batch.
        const fallbackPagination = { type: 'forward', first: API_MAX_PAGE_SIZE } as const

        if (batchableFilter.value.length <= batchSize) {
            return isNil(pagination)
                ? fetcher({ ...params, query: { ...query, pagination: fallbackPagination } })
                : fetcher(params)
        }

        const chunks = chunk(batchableFilter.value, batchSize)
        const results: ({ body?: TItem[] } | undefined)[] = []
        const signal: unknown = get(params, 'signal')

        for (const chunkGroup of chunk(chunks, EXPORT_BATCH_CONCURRENCY)) {
            const groupResults = await Promise.all(
                chunkGroup.map((chunkValue) => {
                    const updatedFilters = [...filters]
                    updatedFilters[batchableIndex] = {
                        ...batchableFilter,
                        value: chunkValue
                    }

                    let chunkPagination: QueryStringInput['pagination'] = fallbackPagination
                    if (pagination) {
                        chunkPagination =
                            'last' in pagination
                                ? { ...pagination, last: chunkValue.length }
                                : { ...pagination, first: chunkValue.length }
                    }

                    const chunkedParams: TParams = {
                        ...params,
                        query: {
                            ...query,
                            pagination: chunkPagination,
                            filters: updatedFilters
                        }
                    }

                    return fetchWithRetry(() => {
                        if (signal instanceof AbortSignal) {
                            signal.throwIfAborted()
                        }
                        return fetcher(chunkedParams)
                    })
                })
            )
            results.push(...groupResults)
        }

        const valid = results.filter((result): result is { body?: TItem[] } => !isNil(result))

        if (isEmpty(valid)) return undefined

        return {
            ...valid[0],
            body: valid.flatMap((r) => r.body ?? [])
        }
    }
}

// makalu-api answers 20 items unless asked for more, and accepts 100 at most.
export const MAKALU_API_MAX_PAGE_SIZE = 100

export async function fetchAllPages<TItem>(
    fetchPage: (q: QueryStringInput, signal?: AbortSignal) => Promise<RequestResponse<TItem[]> | undefined>,
    baseQuery: QueryStringInput = {},
    signal?: AbortSignal,
    pageSize = MAKALU_API_MAX_PAGE_SIZE
): Promise<TItem[]> {
    let after: string | undefined = undefined
    let hasNextPage = true
    const all: TItem[] = []

    while (hasNextPage) {
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError')
        }

        const q: QueryStringInput = { ...baseQuery, pagination: { first: pageSize, after } }

        const res = await fetchPage(q, signal)
        const items = res?.body ?? []
        if (!isEmpty(items)) all.push(...items)

        hasNextPage = !!res?.pagination?.hasNextPage
        after = res?.pagination?.endCursor
    }

    return all
}
